/**
 * The equipment type catalog, offered when a company is created.
 *
 * **Real data, from the maintainer's own source document**
 * (`construction_equipment_maintenance_catalog.xlsx`), which is also where
 * FleetTrack's `equipment_catalog` table was loaded from. Twenty types with
 * their typical sizes, the training an operator needs, a maintenance
 * schedule at four intervals, and the regulation each one cites.
 *
 * An earlier version of this file shipped names and categories I had
 * written myself, with the maintenance fields deliberately left empty and
 * a note saying why: writing "check the hydraulics daily" from general
 * knowledge and presenting it in a safety-adjacent field would be
 * inventing instructions somebody might follow. **That reasoning was
 * right and the premise was wrong** — the guidance was never ours to
 * invent because it already existed, sourced. The note is kept here
 * because the rule still holds for the next field like it.
 *
 * `sourceUrl` is carried per type rather than being a footnote, and it is
 * shown on the screen. A maintenance schedule somebody is asked to follow
 * should say what it rests on, and these rest on OSHA 1910.178, 1926.453,
 * 1926.600, 1926.602 and 1926.1412 — see the Sources sheet of the
 * workbook.
 *
 * **Not a compliance product.** These are the maintainer's own notes
 * against public regulations, not legal advice, and the schedule a
 * particular machine needs is its manufacturer's. The screen says so.
 */
export interface StarterEquipmentType {
  /** Stable, so a company created twice gets the same ids. */
  readonly id: string;
  readonly name: string;
  readonly category: string;
  readonly typicalSizes?: string;
  /** The icon FleetTrack drew for this type; kept so the two agree. */
  readonly iconType?: string;
  readonly certification?: string;
  readonly dailyMaintenance?: string;
  readonly beforeUseInspection?: string;
  readonly afterUse?: string;
  readonly monthlyMaintenance?: string;
  readonly yearlyMaintenance?: string;
  /** The regulation this type's guidance cites. Shown with it. */
  readonly sourceUrl?: string;
}

export const STARTER_EQUIPMENT_TYPES: readonly StarterEquipmentType[] = [
  {
    id: 'eqt-backhoe-loader',
    name: 'Backhoe Loader',
    category: 'Earthmoving / Utility',
    typicalSizes: '14–16 ft dig depth, 4x4',
    iconType: 'backhoe',
    certification:
      'Employer-authorized competent operator; site-specific training; state CDL only if road-driven over threshold',
    dailyMaintenance:
      'Check fluids, tires, pins/bushings, hydraulic leaks, lights, backup alarm, bucket teeth',
    beforeUseInspection:
      'Walkaround; controls/brakes; stabilizers; loader/backhoe pins; leak check; seat belt/ROPS',
    afterUse: 'Clean bucket; grease high-use pins; record hours and damage; fuel/DEF',
    monthlyMaintenance:
      'Inspect hoses, cylinders, brakes, tires, filters, battery; torque wheel lugs',
    yearlyMaintenance:
      'Full PM service, hydraulic oil sampling, brake/steering inspection, ROPS/structure review',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-mini-excavator',
    name: 'Mini Excavator',
    category: 'Earthmoving',
    typicalSizes: '3,000–12,000 lb',
    iconType: 'excavator',
    certification:
      'Employer-authorized competent operator; trenching/excavation awareness if used near trenches',
    dailyMaintenance: 'Check tracks, sprockets, boom/stick pins, fluids, leaks, quick coupler lock',
    beforeUseInspection:
      'Walkaround; attachment secure; swing area clear; controls; alarms; seat belt',
    afterUse: 'Clean undercarriage; lower attachments; grease pins; note fault codes',
    monthlyMaintenance: 'Track tension, final drives, hydraulic hoses, filters, coolant, battery',
    yearlyMaintenance:
      'Comprehensive PM; hydraulic/engine oil service; structural crack inspection',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-crawler-excavator',
    name: 'Crawler Excavator',
    category: 'Earthmoving',
    typicalSizes: '20–50 ton',
    iconType: 'excavator',
    certification:
      'Employer-authorized competent operator; rigging/signal training if lifting loads',
    dailyMaintenance:
      'Daily fluid levels, undercarriage, hydraulic leaks, bucket teeth, cab glass, fire extinguisher',
    beforeUseInspection:
      'Pre-use walkaround; horn/camera/alarms; attachment coupler; swing/boom functions',
    afterUse: 'Clean tracks; park safely; lower bucket; log hours/issues',
    monthlyMaintenance:
      'Undercarriage wear, slew bearing, hydraulic cylinders, filters, fuel system',
    yearlyMaintenance: 'Annual major PM, oil analysis, structural boom/stick inspection',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-skid-steer-loader',
    name: 'Skid Steer Loader',
    category: 'Earthmoving / Material Handling',
    typicalSizes: '1,750–3,500 lb ROC',
    iconType: 'generic',
    certification: 'Employer-authorized trained operator; attachment-specific training',
    dailyMaintenance:
      'Check tires/tracks, lift arms, pins, coupler, fluids, leaks, safety interlocks',
    beforeUseInspection:
      'Inspect attachment lock; lap bar/interlocks; backup alarm; lights; hydraulics',
    afterUse: 'Clean debris from engine bay; lower arms; record attachment hours',
    monthlyMaintenance: 'Hydraulic filters, chaincase oil, tire/track wear, battery, brakes',
    yearlyMaintenance: 'Full PM, hydraulic service, ROPS/FOPS inspection',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-compact-track-loader',
    name: 'Compact Track Loader',
    category: 'Earthmoving / Grading',
    typicalSizes: '2,000–4,000 lb ROC',
    iconType: 'generic',
    certification: 'Employer-authorized trained operator; attachment-specific training',
    dailyMaintenance: 'Track condition, rollers, idlers, pins, fluids, air filter, hydraulic leaks',
    beforeUseInspection: 'Walkaround; attachment/coupler lock; controls/interlocks; mirrors/camera',
    afterUse: 'Clean undercarriage; remove mud from tracks; grease pins; log defects',
    monthlyMaintenance: 'Track tension, roller/idler wear, hydraulic hoses, filters',
    yearlyMaintenance: 'Annual PM and undercarriage inspection; replace worn safety decals',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-wheel-loader',
    name: 'Wheel Loader',
    category: 'Material Handling',
    typicalSizes: '2–6 yd bucket',
    iconType: 'generic',
    certification: 'Employer-authorized trained operator; quarry/mine rules if applicable',
    dailyMaintenance:
      'Tires, articulation joint, bucket edge, fluids, brake pressure, lights/alarms',
    beforeUseInspection:
      'Pre-use walkaround; steering/brakes; horn; backup alarm; bucket functions',
    afterUse: 'Clean radiator screens; lower bucket; fuel/DEF; note damage',
    monthlyMaintenance: 'Articulation pins, brakes, axles, hydraulic system, filters',
    yearlyMaintenance: 'Annual PM, oil sampling, brake/steering system inspection',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-telehandler',
    name: 'Telehandler',
    category: 'Material Handling',
    typicalSizes: '5,500–12,000 lb, 19–55 ft reach',
    iconType: 'forklift',
    certification:
      'Powered industrial truck/forklift training and employer certification; attachment/load chart training',
    dailyMaintenance: 'Tires, forks/carriage, boom chains, stabilizers, fluid levels, leaks',
    beforeUseInspection: 'Inspect forks/boom; load chart present; outriggers; controls; alarms',
    afterUse: 'Lower boom; park secure; charge/fuel; log hours and defects',
    monthlyMaintenance: 'Fork wear, boom wear pads/chains, hydraulic hoses, brakes',
    yearlyMaintenance: 'Annual PM; boom/chain inspection; load handling components review',
    sourceUrl: 'https://www.osha.gov/etools/powered-industrial-trucks/training',
  },
  {
    id: 'eqt-warehouse-forklift',
    name: 'Warehouse Forklift',
    category: 'Material Handling',
    typicalSizes: '3,000–10,000 lb',
    iconType: 'forklift',
    certification:
      'OSHA powered industrial truck training and employer certification; re-evaluation as required',
    dailyMaintenance: 'Forks, mast chains, tires, battery/propane, horn, lights, brakes',
    beforeUseInspection:
      'Pre-shift inspection; seat belt; data plate; leaks; controls; parking brake',
    afterUse: 'Plug in/propane off; clean debris; report damage; park forks down',
    monthlyMaintenance: 'Mast chains, hydraulic fluid, brake adjustment, tires, battery watering',
    yearlyMaintenance: 'Annual PM; capacity plate review; mast/fork inspection',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.178',
  },
  {
    id: 'eqt-rough-terrain-forklift',
    name: 'Rough Terrain Forklift',
    category: 'Material Handling',
    typicalSizes: '6,000–12,000 lb',
    iconType: 'forklift',
    certification:
      'Powered industrial truck training and employer certification; rough terrain and load chart training',
    dailyMaintenance: 'Tires, forks, mast/boom, outriggers, fluids, hydraulic leaks, alarms',
    beforeUseInspection: 'Pre-use controls/brakes; load chart; terrain hazards; attachment lock',
    afterUse: 'Clean mud; park level; lower forks; log damage',
    monthlyMaintenance: 'Axles, brakes, steering, mast/boom components, hydraulic hoses',
    yearlyMaintenance: 'Annual PM; structural inspection; safety systems review',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.602',
  },
  {
    id: 'eqt-articulating-boom-lift',
    name: 'Articulating Boom Lift',
    category: 'Aerial Work Platform',
    typicalSizes: '30–80 ft',
    iconType: 'lift',
    certification:
      'Employer-authorized aerial lift operator training; fall protection training; harness/lanyard use per site rules',
    dailyMaintenance:
      'Tires, basket, guardrails, controls, emergency descent, batteries/fuel, leaks',
    beforeUseInspection:
      'Function test ground/platform controls; inspect boom cradle/outriggers; harness anchor',
    afterUse: 'Charge/fuel; lower/stow boom; clean basket; record issues',
    monthlyMaintenance: 'Hydraulic hoses, pins, batteries, limit switches, alarms, tires',
    yearlyMaintenance: 'Annual inspection by qualified person; structural and function inspection',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.453',
  },
  {
    id: 'eqt-telescopic-boom-lift',
    name: 'Telescopic Boom Lift',
    category: 'Aerial Work Platform',
    typicalSizes: '40–120 ft',
    iconType: 'lift',
    certification: 'Employer-authorized aerial lift operator training; fall protection training',
    dailyMaintenance: 'Tires, boom sections, basket, controls, emergency lowering, fluids, leaks',
    beforeUseInspection: 'Function test; check platform gate, alarms, tilt sensor, lanyard points',
    afterUse: 'Stow boom; charge/fuel; remove debris; log hours and damage',
    monthlyMaintenance: 'Boom wear pads, cylinders, batteries, limit switches, drive system',
    yearlyMaintenance: 'Annual qualified inspection; structural crack/weld inspection',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.453',
  },
  {
    id: 'eqt-scissor-lift',
    name: 'Scissor Lift',
    category: 'Aerial Work Platform',
    typicalSizes: '19–45 ft electric/rough terrain',
    iconType: 'scissor',
    certification:
      'Employer-authorized MEWP/scissor-lift training; fall protection per employer/site policy',
    dailyMaintenance:
      'Rails/gate, pothole protection, tires, controls, batteries, leaks, emergency stop',
    beforeUseInspection:
      'Function test lift/drive; tilt alarm; platform extension lock; charger cord',
    afterUse: 'Charge batteries; lower platform; clean deck; report defects',
    monthlyMaintenance: 'Batteries, chargers, lift arms, pins, hydraulic system, tires',
    yearlyMaintenance: 'Annual qualified inspection; structural and safety system review',
    sourceUrl: 'https://www.osha.gov/laws-regs/standardinterpretations/1992-10-23-0',
  },
  {
    id: 'eqt-bulldozer',
    name: 'Bulldozer',
    category: 'Earthmoving / Grading',
    typicalSizes: 'D3–D8 class',
    iconType: 'dozer',
    certification: 'Employer-authorized competent operator; slope/grade and seatbelt/ROPS training',
    dailyMaintenance: 'Tracks, blade/C-frame, fluids, leaks, lights, backup alarm, cab glass',
    beforeUseInspection: 'Walkaround; controls; brakes; blade/ripper functions; ROPS/seat belt',
    afterUse: 'Clean undercarriage; lower blade/ripper; grease; log wear/damage',
    monthlyMaintenance:
      'Track tension, rollers/idlers, hydraulic cylinders, filters, cooling package',
    yearlyMaintenance: 'Annual PM; undercarriage assessment; structural inspection',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-motor-grader',
    name: 'Motor Grader',
    category: 'Grading',
    typicalSizes: '12–14 ft moldboard',
    iconType: 'grader',
    certification:
      'Employer-authorized competent operator; roadwork/traffic control training if needed',
    dailyMaintenance: 'Tires, moldboard/circle, fluids, leaks, lights, articulation, backup alarm',
    beforeUseInspection: 'Pre-use brakes/steering; blade controls; mirrors/cameras; seat belt',
    afterUse: 'Clean moldboard; park blade down; fuel/DEF; record hours',
    monthlyMaintenance: 'Circle wear, blade cutting edges, hydraulic hoses, brakes, axles',
    yearlyMaintenance: 'Annual PM; steering/brake inspection; structural frame review',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-dump-truck',
    name: 'Dump Truck',
    category: 'Hauling',
    typicalSizes: '10–18 yd tandem/tri-axle',
    iconType: 'truck',
    certification:
      'CDL required for public-road commercial operation; site-specific equipment training',
    dailyMaintenance: 'Tires, brakes, lights, horn, backup alarm, dump bed, hydraulic leaks',
    beforeUseInspection:
      'Pre-trip/inspection; bed down; tailgate latch; mirrors/camera; load secure',
    afterUse: 'Clean bed; refuel; inspect for damage; log mileage/hours',
    monthlyMaintenance: 'Brake system, tires, suspension, hoist cylinder, PTO, fluids',
    yearlyMaintenance: 'Annual DOT/state inspection if road vehicle; full PM and hoist inspection',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-concrete-mixer-truck',
    name: 'Concrete Mixer Truck',
    category: 'Concrete',
    typicalSizes: '8–11 yd',
    iconType: 'mixer',
    certification: 'CDL for public-road commercial operation; site and washout training',
    dailyMaintenance: 'Drum, chute, tires, brakes, lights, water tank, hydraulic leaks',
    beforeUseInspection: 'Pre-trip; drum controls; chute locks; washout kit; backup alarm',
    afterUse: 'Wash drum/chutes; clean spills; log yardage/hours',
    monthlyMaintenance: 'Hydraulic system, drum rollers, chute hardware, brakes, tires',
    yearlyMaintenance: 'Annual DOT/state inspection if road vehicle; drum/roller PM',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-vibratory-roller-compactor',
    name: 'Vibratory Roller / Compactor',
    category: 'Compaction',
    typicalSizes: '36–84 in drum',
    iconType: 'roller',
    certification: 'Employer-authorized trained operator; compaction/slope training',
    dailyMaintenance: 'Drum/scrapers, tires, vibration system, fluids, leaks, ROPS/seat belt',
    beforeUseInspection: 'Walkaround; brakes/steering; vibration control; water spray system',
    afterUse: 'Clean drum; inspect scrapers; park brake; log hours',
    monthlyMaintenance: 'Vibration bearings, hydraulic hoses, water system, filters',
    yearlyMaintenance: 'Annual PM; vibration system inspection; ROPS/structure check',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-trencher',
    name: 'Trencher',
    category: 'Utility / Excavation',
    typicalSizes: 'Walk-behind to ride-on, 24–72 in depth',
    iconType: 'trencher',
    certification:
      'Employer-authorized operator; excavation, utility locate, and trench safety awareness',
    dailyMaintenance: 'Chain/teeth, boom, tires/tracks, shields, fluids, leaks, emergency stop',
    beforeUseInspection:
      'Utility locate confirmed; guards; controls; chain tension; work zone clear',
    afterUse: 'Clean chain/boom; grease; lower boom; record tooth wear',
    monthlyMaintenance: 'Chain wear, sprockets, bearings, hydraulics, filters',
    yearlyMaintenance: 'Annual PM; guard/safety device inspection; structural review',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
  {
    id: 'eqt-mobile-crane',
    name: 'Mobile Crane',
    category: 'Lifting',
    typicalSizes: '30–120 ton hydraulic truck crane',
    iconType: 'crane',
    certification:
      'Crane operator certification/qualification when applicable; signalperson/rigger qualification; employer authorization',
    dailyMaintenance: 'Wire rope, hook/latch, tires/outriggers, fluids, anti-two-block, load chart',
    beforeUseInspection:
      'Pre-shift competent-person inspection; controls; outriggers; LMI; swing area',
    afterUse: 'Stow boom; secure rigging; log lifts/issues; fuel/DEF',
    monthlyMaintenance: 'Wire rope, sheaves, hydraulics, brakes, LMI calibration checks',
    yearlyMaintenance: 'Annual comprehensive inspection by qualified person',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.1412',
  },
  {
    id: 'eqt-asphalt-paver',
    name: 'Asphalt Paver',
    category: 'Paving',
    typicalSizes: '8–20 ft screed',
    iconType: 'paver',
    certification:
      'Employer-authorized trained operator; paving crew hazard/traffic control training',
    dailyMaintenance: 'Screed, conveyors/augers, tracks/tires, propane/heaters, fluids, leaks',
    beforeUseInspection:
      'Function test controls; emergency stops; screed heat; guards; backup alarm',
    afterUse: 'Clean asphalt from hopper/screed; lubricate; log tons/hours',
    monthlyMaintenance: 'Conveyors, augers, screed plates, hydraulic system, filters',
    yearlyMaintenance: 'Annual PM; screed/auger inspection; safety systems review',
    sourceUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.600',
  },
];
